import React, { useContext, useRef, useState } from 'react'
import { useForm } from "react-hook-form";
import { Eye, EyeOff } from 'lucide-react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { UserContext } from '../../Contexts/UserContext';
import { changePasswordUrl } from '../../Config/config';
import Constants from '../../Constants';

/**
 * The `ResetPasswordSetting` component renders the in-app reset password form with a new password
 * field, a confirm password field and a save button.
 * @returns a JSX element.
 */
const ResetPasswordSetting = () => {

    const { getUser, logout } = useContext(UserContext);

    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [isSaving, setIsSaving] = useState(false);

    const { register, handleSubmit, watch, reset, formState: { errors } } = useForm({
        mode: "onChange",
        defaultValues: {
            newPassword: "",
            confirmPassword: ""
        }
    });

    const password = useRef({});
    password.current = watch("newPassword", "");

    /**
     * The function `onSubmitForResetPW` posts the new password of the logged in user to the change
     * password api and resets the form on success.
     * @param formData - the validated new password and confirm password values.
     */
    const onSubmitForResetPW = (formData) => {
        setIsSaving(true);
        axios.post(changePasswordUrl, {
            email: getUser()?.email,
            password: formData.newPassword,
            password_confirmation: formData.confirmPassword,
        }, {
            headers: {
                "Content-Type": "application/json"
            }
        })
            .then((response) => {
                if (response?.data?.success) {
                    toast.success(response?.data?.message ?? "Your password has been updated");
                    reset();
                } else {
                    toast.error(response?.data?.message ?? "Unable to update password");
                }
            })
            .catch((error) => {
                if (error?.response?.status === Constants.sessionTerminatedStatus) {
                    logout();
                    return;
                }
                toast.error(error?.response?.data?.message ?? "Unable to update password");
                console.log("API not working", error);
            })
            .finally(() => {
                setIsSaving(false);
            });
    }

    return (
        <div className='pt-3 lg:pt-0'>
            <div className='md:grid md:grid-cols-7 lg:grid-cols-5 lg:gap-4'>
                <div className='md:col-span-2 lg:col-span-1 md:bg-[#FAF5EA] bg-[#F5F5F5] md:shadow md:h-screen'>
                    <p className="px-5 mb-3 mt-3 text-[#2B2A29] leading-[22px] text-[18px] font-sans_medium_body tracking-[-0.39px]">
                        Account Setting
                    </p>
                    <div className='md:block hidden'>
                        <a className="group flex items-center px-5 py-3 text-[15px] font-medium block leading-[22px] border-r-4 border-[#F1CA75] bg-white text-[#F1CA75] font-sans_medium_body"
                            onClick={e => e.preventDefault()}
                            href="#link1"
                            role="tablist"
                        >
                            <span className="font-sans_book_body text-[15px] leading-[18px] tracking-[-0.32px] capitalize">
                                Reset Password
                            </span>
                        </a>
                    </div>
                </div>

                <div className='md:col-span-5 lg:col-span-4 px-3'>
                    <div className="bg-white lg:shadow rounded-[4px] lg:mt-5 pb-6">
                        <p className="py-4 px-4 font-sans_medium_body text-[16px] text-[#2B2A29] leading-[19px] tracking-[-0.34px]">
                            Reset Password
                        </p>
                        <div className="border-t border-[#E3E3E3]"></div>

                        <form className="px-4 mt-6 space-y-6 max-w-[420px]" onSubmit={e => e.preventDefault()}>
                            <div className="space-y-1">
                                <label htmlFor="newPassword" className="block mb-2 font-sans_book_body text-[14px] text-[#2B2A29] tracking-[-0.3px] leading-[17px]">
                                    New Password
                                </label>
                                <div className="relative">
                                    <input
                                        {...register("newPassword", {
                                            required: "Please enter your new password",
                                            minLength: {
                                                value: 5,
                                                message: "Min length should be greater than 5"
                                            }
                                        })}
                                        id="newPassword"
                                        name="newPassword"
                                        type={showNewPassword ? "text" : "password"}
                                        autoComplete="off"
                                        className="form-input shadow-sm bg-[#FDF7E9] focus:outline-none rounded-[5px] focus:ring-[#BC8B20] border border-[#BC8B20] py-1.5 px-2 w-full block pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowNewPassword(!showNewPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {!showNewPassword ? (
                                            <EyeOff size={20} className="text-gray-500" />
                                        ) : (
                                            <Eye size={20} className="text-gray-500" />
                                        )}
                                    </button>
                                </div>
                                <p className="text-[red] text-[12px]">{errors.newPassword?.message}</p>
                            </div>

                            <div className="space-y-1">
                                <label htmlFor="confirmPassword" className="block mb-2 font-sans_book_body text-[14px] text-[#2B2A29] tracking-[-0.3px] leading-[17px]">
                                    Confirm New Password
                                </label>
                                <div className="relative">
                                    <input
                                        {...register("confirmPassword", {
                                            required: "Please re-enter your new password",
                                            validate: value =>
                                                value === password?.current || "The passwords do not match"
                                        })}
                                        id="confirmPassword"
                                        name="confirmPassword"
                                        type={showConfirmPassword ? "text" : "password"}
                                        autoComplete="off"
                                        className="form-input shadow-sm bg-[#FDF7E9] focus:outline-none rounded-[5px] focus:ring-[#BC8B20] border border-[#BC8B20] py-1.5 px-2 w-full block pr-10"
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                        className="absolute inset-y-0 right-0 pr-3 flex items-center"
                                    >
                                        {!showConfirmPassword ? (
                                            <EyeOff size={20} className="text-gray-500" />
                                        ) : (
                                            <Eye size={20} className="text-gray-500" />
                                        )}
                                    </button>
                                </div>
                                <p className="text-[red] text-[12px] leading-[23px]">{errors.confirmPassword?.message}</p>
                            </div>

                            <div>
                                <button
                                    type="submit"
                                    onClick={handleSubmit(onSubmitForResetPW)}
                                    disabled={isSaving}
                                    className="px-8 py-2.5 border border-transparent rounded-[5px] shadow-sm text-[14px] text-[#FFFFFF] tracking-[-0.3px] leading-[17px] bg-[#A5711A] hover:bg-[#BC8B20] focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-60"
                                >
                                    {isSaving ? "Saving..." : "Save"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ResetPasswordSetting
